import { format } from 'date-fns'

/**
 * Formats the MAC address value.
 *
 */
export function formatMacAddress(value: string): string {
  const matches = value.match(/.{1,2}/g)

  return matches !== null ? matches.join(':').toUpperCase() : value
}

/**
 * Formats the temperature value (°C).
 */
export function formatTemperature(value: number | null, unit = true, decimals = 1): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue}°C` : formattedValue
}

/**
 * Formats the temperature as delta value (°C).
 */
export function formatTemperatureDelta(value: number | null, unit = true, decimals = 1): string {
  if (value === null) {
    return '-'
  }

  const roundedValue = roundValue(value, decimals)

  const sign = roundedValue > 0 ? '+' : ''
  const formattedValue = unit
    ? `${roundedValue.toFixed(decimals)}°C`
    : roundedValue.toFixed(decimals)

  return `${sign}${formattedValue}`
}

/**
 * Formats the humidity value (%).
 */
export function formatHumidity(value: number | null, unit = true, decimals = 0): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue}%` : formattedValue
}

/**
 * Formats the humidity as delta value (%)
 */
export function formatHumidityDelta(value: number, unit = true, decimals = 1): string {
  const roundedValue = roundValue(value, decimals)

  const sign = roundedValue > 0 ? '+' : ''
  const formattedValue = unit
    ? `${roundedValue.toFixed(decimals)}%`
    : roundedValue.toFixed(decimals)

  return `${sign}${formattedValue}`
}

/**
 * Formats the pressure value (hPa).
 */
export function formatPressure(value: number | null, unit = true, decimals = 0): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} hPa` : formattedValue
}

/**
 * Formats the pressure as delta value (hPa).
 */
export function formatPressureDelta(
  value: number,
  unit = true,
  decimals = 1,
  unitText = 'hPa'
): string {
  const roundedValue = roundValue(value, decimals)

  const sign = roundedValue > 0 ? '+' : ''
  const formattedValue = unit
    ? `${roundedValue.toFixed(decimals)} ${unitText}`
    : roundedValue.toFixed(decimals)

  return `${sign}${formattedValue}`
}

/**
 * Formats the percentage value (%).
 */
export function formatPercentage(value: number | null, unit = true, decimals = 0): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue}%` : `${formattedValue}`
}

/**
 * Formats the percentage as delta value (%).
 */
export function formatPercentageDelta(value: number, unit = true, decimals = 1): string {
  const roundedValue = roundValue(value, decimals)

  const sign = roundedValue > 0 ? '+' : ''
  const formattedValue = unit
    ? `${roundedValue.toFixed(decimals)}%`
    : roundedValue.toFixed(decimals)

  return `${sign}${formattedValue}`
}

/**
 * Formats the degress value (°).
 */
export function formatDegress(value: number, unit = true, decimals = 1): string {
  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue}°` : formattedValue
}

/**
 * Formats the energy consumption value (kWh).
 */
export function formatEnergyConsumption(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} kWh` : formattedValue
}

/**
 * Formats the power value (kW).
 */
export function formatPower(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} kW` : formattedValue
}

/**
 * Formats the energy emission value (g/CO2eq).
 */
export function formatEnergyEmission(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} g/CO₂eq` : formattedValue
}

/**
 * Formats the energy price value (c/kWh).
 */
export function formatEnergyPrice(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} c/kWh` : formattedValue
}

/**
 * Format the emission factor value (gCO2eq/kWh).
 */
export function formatEmissionFactor(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} gCO2eq/kWh` : formattedValue
}

/**
 * Formats the price value value (€).
 */
export function formatPrice(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} €` : formattedValue
}

export function formatApperentPower(value: number | null, unit = true): string {
  if (value === null) {
    return '-'
  }

  return unit ? `${value} kVA` : `${value}`
}

/**
 * Formats the current value value (A).
 */
export function formatCurrent(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} A` : formattedValue
}

export function formatNominalCurrent(value: number | null, unit = true): string {
  if (value === null) {
    return '-'
  }
  // If the value is decimal then use precision otherwise 0 decimals.
  return unit ? `${value} A` : `${value}`
}

export function formatKilovoltampere(value: number | null, unit = true, decimals = 1): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} kVA` : formattedValue
}

/**
 * Formats the voltage value (V)
 */
export function formatVoltage(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} V` : formattedValue
}

export function formatNominalVoltage(value: number | null, unit = true): string {
  if (value === null) {
    return '-'
  }
  // If the value is decimal then use precision otherwise 0 decimals.
  return unit ? `${value} V` : `${value}`
}

/**
 * Formats the battery voltage value (mV).
 */
export function formatBatteryVoltage(value: number | null, unit = true, decimals = 2): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} mV` : formattedValue
}

/**
 * Formats the RSSI value (dBm).
 */
export function formatRssi(value: number | null, unit = true, decimals = 0): string {
  if (value === null) {
    return '-'
  }

  const formattedValue = formatValue(value, decimals)

  return unit ? `${formattedValue} dBm` : formattedValue
}

/**
 * Round a value to specified decimals.
 */
export function roundValue(value: number, decimals: number): number {
  const precision = Math.pow(10, decimals)

  return Math.round(value * precision) / precision
}

/**
 * Format a value to specified decimals.
 */
export function formatValue(value: number | null, decimals: number): string {
  if (value === null) {
    return '-'
  }

  const roundedValue = roundValue(value, decimals)

  return roundedValue.toFixed(decimals)
}

export function formatTimestamp(timestamp: Date, seconds = false) {
  return seconds ? format(timestamp, 'd.M.yyyy HH:mm:ss') : format(timestamp, 'd.M.yyyy HH:mm')
}
